import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { usePeople, useWork, useWorkEvents, type WorkItem } from "../api/hooks";
import { Button, Chip, Empty, ErrorBox, Kpi, Loading, Page, Table, statusTone } from "../components/ui";
import { fmtDate, fmtDateTime, relative, title } from "../lib/format";
import { metaFor } from "../lib/connectors";

export const Route = createFileRoute("/work")({ component: Work });

const kinds = ["", "pull_request", "merge_request", "issue", "ticket", "conversation", "deal", "opportunity", "account", "board_item"];

/** Live (what is open and stuck) or done (what closed this week, by person). */
type View = "live" | "done";

/** Monday 00:00 of the current week, in the viewer's zone. */
function thisMonday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

function Work() {
  const [view, setView] = useState<View>("live");
  const [kind, setKind] = useState("");
  const [openOnly, setOpenOnly] = useState(true);
  const [stale, setStale] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const done = view === "done";
  const work = useWork(done ? { kind: kind || undefined, open_only: false, limit: 500 } : { kind: kind || undefined, open_only: openOnly, stale_days: stale ? 7 : undefined, limit: 200 });
  const people = usePeople();
  const nameOf = (key: string | null | undefined) => (key ? (people.data?.find((p) => p.person_key === key)?.full_name ?? key) : "unresolved");
  const monday = thisMonday();
  const rows = done ? (work.data ?? []).filter((w) => w.closed_at && new Date(w.closed_at) >= monday).sort((a, b) => (a.closed_at! < b.closed_at! ? 1 : -1)) : (work.data ?? []);
  return (
    <Page
      title="Work"
      sub={done ? "What closed this week, by person: merged, resolved, won. Counts are items, not effort." : "Live work by person across engineering, sales and customer tools: what is open, what is stuck, what is overdue."}
      actions={
        <>
          <select className="input !w-auto" value={kind} onChange={(e) => setKind(e.target.value)}>{kinds.map((k) => <option key={k} value={k}>{k ? title(k) : "All kinds"}</option>)}</select>
          <Button variant={done ? "dark" : "light"} onClick={() => setView((v) => (v === "done" ? "live" : "done"))}>Done this week</Button>
          {!done && <Button variant={openOnly ? "dark" : "light"} onClick={() => setOpenOnly((v) => !v)}>Open only</Button>}
          {!done && <Button variant={stale ? "dark" : "light"} onClick={() => setStale((v) => !v)}>Stuck &gt; 7d</Button>}
        </>
      }
    >
      {work.isPending ? (
        <Loading />
      ) : work.isError ? (
        <ErrorBox error={work.error} />
      ) : rows.length === 0 ? (
        done ? (
          <Empty title="Nothing closed yet this week" body={`Items that closed since ${fmtDate(monday.toISOString())} land here as the connectors sync.`} />
        ) : (
          <Empty title="No work items" body="Connect GitHub, Jira, HubSpot or a support tool; items appear after the first sync." />
        )
      ) : (
        <>
          {done && <DoneByPerson rows={rows} nameOf={nameOf} />}
          <Table
            rows={rows}
            keyOf={(w) => w.item_key}
            cols={[
              { h: "Item", cell: (w: WorkItem) => <><a className="font-semibold" href={w.url ?? undefined} target="_blank" rel="noreferrer">{w.title ?? w.source_id}</a><span className="sub">{metaFor(w.connector).name} · {title(w.kind)} · {w.source_id}</span></> },
              { h: "Owner", cell: (w) => <span className="text-muted">{nameOf(w.owner_person_key)}</span>, w: "140px" },
              { h: "Status", cell: (w) => <Chip tone={statusTone(w.outcome ?? w.status)}>{title(w.outcome ?? w.status ?? "open")}</Chip>, w: "130px" },
              { h: "Amount", cell: (w) => <span className="text-muted">{w.amount != null ? `${w.amount.toLocaleString()} ${w.currency ?? ""}` : "—"}</span>, w: "120px" },
              { h: "Opened", cell: (w) => <span className="text-muted">{fmtDate(w.opened_at)}</span>, w: "90px" },
              done
                ? { h: "Closed", cell: (w) => <span className="text-muted">{fmtDate(w.closed_at)}</span>, w: "90px" }
                : { h: "Due", cell: (w) => { const over = w.due_at && !w.closed_at && new Date(w.due_at) < new Date(); return <span className={over ? "text-red font-semibold" : "text-muted"}>{fmtDate(w.due_at)}</span>; }, w: "90px" },
              { h: "Updated", cell: (w) => <span className="text-muted">{relative(w.updated_at)}</span>, w: "100px" },
              { h: "", cell: (w) => <Button variant="light" className="!py-1 !px-2.5 !text-[12px]" onClick={() => setSelected(selected === w.item_key ? null : w.item_key)}>{selected === w.item_key ? "Hide" : "History"}</Button>, w: "90px" },
            ]}
          />
        </>
      )}
      {selected && <Events itemKey={selected} />}
    </Page>
  );
}

/** One tile per person: how many items they closed this week, busiest first. */
function DoneByPerson({ rows, nameOf }: { rows: WorkItem[]; nameOf: (key: string | null | undefined) => string }) {
  const counts = new Map<string, number>();
  for (const w of rows) {
    const k = w.owner_person_key ?? "";
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const tiles = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
      <Kpi label="Closed this week" value={rows.length} unit="items" />
      {tiles.map(([k, n]) => <Kpi key={k || "unresolved"} label={k ? nameOf(k) : "Unresolved"} value={n} unit={n === 1 ? "item" : "items"} />)}
    </div>
  );
}

function Events({ itemKey }: { itemKey: string }) {
  const ev = useWorkEvents(itemKey);
  if (ev.isPending) return <Loading />;
  if (ev.isError) return <ErrorBox error={ev.error} />;
  return (
    <div className="mt-3">
      <Table
        rows={ev.data}
        keyOf={(e) => `${e.event_at}${e.event_type}${e.to_value ?? ""}`}
        empty="No history for this item"
        cols={[
          { h: "When", cell: (e) => fmtDateTime(e.event_at), w: "170px" },
          { h: "Event", cell: (e) => title(e.event_type), w: "160px" },
          { h: "From", cell: (e) => <span className="text-muted">{e.from_value ?? "—"}</span> },
          { h: "To", cell: (e) => e.to_value ?? "—" },
          { h: "By", cell: (e) => <span className="text-muted">{e.actor_person_key ?? "—"}</span>, w: "140px" },
        ]}
      />
    </div>
  );
}
